<div class="flex flex-col h-full">
  <!-- Header with Search -->
  <div class="flex items-center gap-4 p-4 border-b">
    <mat-form-field appearance="outline" class="flex-1 !mb-0">
      <input
        matInput
        [formControl]="searchControl"
        placeholder="Nombre, código o nombre comercial..."
        autocomplete="off">
      <mat-icon matPrefix>search</mat-icon>
      @if (searchControl.value) {
        <button matSuffix mat-icon-button (click)="clearSearch()" type="button">
          <mat-icon>clear</mat-icon>
        </button>
      }
    </mat-form-field>
  </div>

  <!-- Content -->
  <div class="flex-1 overflow-hidden flex flex-col">
    <!-- Loading Indicator -->
    @if (isLoading) {
      <div class="flex items-center justify-center h-full">
        <div class="text-center">
          <mat-spinner diameter="40"></mat-spinner>
          <p class="mt-2 text-gray-500">Cargando clientes...</p>
        </div>
      </div>
    }

    <!-- Clients Table -->
    @if (!isLoading) {
      <div class="overflow-hidden bg-white flex-1 flex flex-col">
        <div class="flex-1 overflow-y-auto">
          <table mat-table [dataSource]="dataSource" multiTemplateDataRows class="w-full">

            <!-- Expand Column -->
            <ng-container matColumnDef="expand">
              <th mat-header-cell *matHeaderCellDef class="w-16"></th>
              <td mat-cell *matCellDef="let client" class="py-2">
                <button
                  mat-icon-button
                  (click)="toggleExpandRow(client); $event.stopPropagation()"
                  [disabled]="!hasRoutes(client)"
                  matTooltip="Ver rutas"
                  class="transition-transform"
                  [class.rotate-90]="expandedElement === client">
                  @if (hasRoutes(client)) {
                    <mat-icon>chevron_right</mat-icon>
                  } @else {
                    <mat-icon class="text-gray-300">block</mat-icon>
                  }
                </button>
              </td>
            </ng-container>

            <!-- Client Info Column -->
            <ng-container matColumnDef="info">
              <th mat-header-cell *matHeaderCellDef class="font-semibold">Cliente</th>
              <td mat-cell *matCellDef="let client" class="py-4">
                <div>
                  <app-business-partner-navigation
                    [businessPartner]="client"
                    displayMode="both"
                    [showIcon]="true"
                    interactionMode="icon-only"
                    [openInNewTab]="true"
                  />
                  @if (client.commercialName) {
                    <p class="text-sm text-gray-500">{{ client.commercialName }}</p>
                  }
                  @if (hasRoutes(client)) {
                    <div class="mt-1">
                      <mat-chip-set class="routes-chip-set">
                        <mat-chip class="!min-h-[24px] !py-0 !text-xs route-total-chip">
                          <mat-icon class="!w-4 !h-4 !text-[14px] mr-1">route</mat-icon>
                          {{ getRoutesCount(client) }} {{ getRoutesCount(client) === 1 ? 'ruta' : 'rutas' }}
                        </mat-chip>
                        @if (getRoutesWithFrequency(client) > 0) {
                          <mat-chip class="!min-h-[24px] !py-0 !text-xs route-with-frequency-chip"
                                    matTooltip="Rutas con frecuencia configurada">
                            <mat-icon class="!w-4 !h-4 !text-[14px] mr-1">check_circle</mat-icon>
                            {{ getRoutesWithFrequency(client) }}
                          </mat-chip>
                        }
                        @if (getRoutesWithoutFrequency(client) > 0) {
                          <mat-chip class="!min-h-[24px] !py-0 !text-xs route-without-frequency-chip"
                                    matTooltip="Rutas sin frecuencia configurada">
                            <mat-icon class="!w-4 !h-4 !text-[14px] mr-1">warning</mat-icon>
                            {{ getRoutesWithoutFrequency(client) }}
                          </mat-chip>
                        }
                      </mat-chip-set>
                    </div>
                  }
                </div>
              </td>
            </ng-container>

            <!-- Location Column -->
            <ng-container matColumnDef="location">
              <th mat-header-cell *matHeaderCellDef class="font-semibold">Ubicación</th>
              <td mat-cell *matCellDef="let client">
                <div class="text-sm">
                  <p class="text-gray-900">{{ getAddressName(client) }}</p>
                  @if (getAddressLocations(client)) {
                    <p class="text-gray-500">{{ getAddressLocations(client) }}</p>
                  }
                </div>
              </td>
            </ng-container>

            <!-- Map Column (no header label; pin sits next to the client name/code) -->
            <ng-container matColumnDef="mapa">
              <th mat-header-cell *matHeaderCellDef class="w-12"></th>
              <td mat-cell *matCellDef="let client" class="text-center">
                <app-address-map-button
                  [geolocationPoint]="client.principalAddress?.geolocationPoint"
                  [customerMongoId]="client._id"
                  [customerId]="client.id"
                  [customerName]="client.commercialName || client.name"
                  [addressName]="client.principalAddress?.name"
                />
              </td>
            </ng-container>

            <!-- Territorios Column -->
            <ng-container matColumnDef="territorios">
              <th mat-header-cell *matHeaderCellDef class="font-semibold">Territorios</th>
              <td mat-cell *matCellDef="let client">
                <div class="text-sm">
                  @if (getTerritories(client).length > 0) {
                    <div class="flex flex-wrap gap-1 items-center">
                      @for (territory of getTerritoriesToDisplay(client).visible; track territory._id) {
                        <span class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                          <mat-icon class="!w-3 !h-3 !text-[12px]">location_on</mat-icon>
                          <span class="truncate">{{ territory.externalKeys?.code ? (territory.externalKeys.code + ' - ' + territory.name) : territory.name }}</span>
                          <app-route-navigation
                            [route]="territory"
                            [showText]="false"
                            [showIcon]="true"
                            interactionMode="icon-only"
                            [openInNewTab]="true"
                          />
                        </span>
                      }

                      @if (getTerritoriesToDisplay(client).hasMore) {
                        <span
                          class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-700 cursor-pointer hover:bg-gray-300"
                          [matMenuTriggerFor]="territoriesMenu"
                          #menuTrigger="matMenuTrigger"
                          (mouseenter)="menuTrigger.openMenu()"
                          (mouseleave)="closeMenuWithDelay(menuTrigger)">
                          +{{ getTerritoriesToDisplay(client).moreCount }} más
                        </span>

                        <mat-menu #territoriesMenu="matMenu" [hasBackdrop]="false">
                          <div
                            class="p-3 max-w-xs max-h-48 overflow-y-auto"
                            (mouseenter)="cancelMenuClose()"
                            (mouseleave)="menuTrigger.closeMenu()">
                            <div class="text-xs font-medium text-gray-500 mb-2">
                              Todos los territorios ({{ getTerritoriesToDisplay(client).total }})
                            </div>
                            <div class="flex flex-wrap gap-1">
                              @for (territory of getTerritories(client); track territory._id) {
                                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                                  <mat-icon class="!w-3 !h-3 !text-[12px]">location_on</mat-icon>
                                  <span class="truncate">{{ territory.externalKeys?.code ? (territory.externalKeys.code + ' - ' + territory.name) : territory.name }}</span>
                                  <app-route-navigation
                                    [route]="territory"
                                    [showText]="false"
                                    [showIcon]="true"
                                    interactionMode="icon-only"
                                    [openInNewTab]="true"
                                  />
                                </span>
                              }
                            </div>
                          </div>
                        </mat-menu>
                      }
                    </div>
                  } @else {
                    <span class="text-gray-400">Sin territorios</span>
                  }
                </div>
              </td>
            </ng-container>

            <!-- Acciones Column -->
            <ng-container matColumnDef="acciones">
              <th mat-header-cell *matHeaderCellDef class="font-semibold">Asignar Territorio</th>
              <td mat-cell *matCellDef="let client">
                <button
                  mat-icon-button
                  color="accent"
                  matTooltip="Asignar Territorio"
                  (click)="openAssignTerritoryDialog(client); $event.stopPropagation()" *canPermissionsOnly="PERMISSIONS.BUSINESS_PARTNER">
                  <mat-icon>add_location</mat-icon>
                </button>
              </td>
            </ng-container>

            <!-- Expanded Detail Row -->
            <ng-container matColumnDef="expandedDetail">
              <td mat-cell *matCellDef="let client" [attr.colspan]="displayedColumns.length">
                <div class="overflow-hidden"
                     [@detailExpand]="client == expandedElement ? 'expanded' : 'collapsed'">
                  <div class="p-4">
                    <h3 class="text-md font-semibold mb-3 text-gray-700">
                      <mat-icon class="inline-block align-middle mr-2">route</mat-icon>
                      Rutas Asignadas
                    </h3>

                    <!-- Routes Table -->
                    <div class="bg-white rounded border border-gray-200 overflow-hidden">
                      <table class="w-full">
                        <thead class="bg-gray-100">
                          <tr>
                            <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Nombre de Ruta</th>
                            <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Función</th>
                            <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Frecuencias</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (subRoute of client.subRoutes; track subRoute._id) {
                            <tr [class.route-with-frequency]="hasFrequencies(subRoute)"
                                [class.route-without-frequency]="!hasFrequencies(subRoute)">
                              <td class="px-4 py-3 text-sm font-medium">
                                <div class="flex items-center gap-2">
                                  @if (hasFrequencies(subRoute)) {
                                    <mat-icon class="frequency-status-icon has-frequency"
                                              matTooltip="Esta ruta tiene frecuencias configuradas">
                                      check_circle
                                    </mat-icon>
                                  } @else {
                                    <mat-icon class="frequency-status-icon no-frequency"
                                              matTooltip="Esta ruta NO tiene frecuencias configuradas">
                                      warning
                                    </mat-icon>
                                  }
                                  <span>{{ subRoute.name }}</span>
                                  <app-sub-route-navigation
                                    [route]="{ routeName: subRoute.name }"
                                    [showText]="false"
                                    [showIcon]="true"
                                    interactionMode="icon-only"
                                    [openInNewTab]="true"
                                  />
                                </div>
                              </td>
                              <td class="px-4 py-3 text-sm">
                                @if (subRoute.function) {
                                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                    {{ subRoute.function.name }}
                                  </span>
                                } @else {
                                  <span class="text-gray-400">Sin función</span>
                                }
                              </td>
                              <td class="px-4 py-3 text-sm">
                                <div class="flex items-center gap-2">
                                  <app-client-frequency-display
                                    [subRouteId]="subRoute._id"
                                    [subRouteNormalId]="subRoute.id"
                                    [businessPartnerId]="client._id"
                                    [businessPartnerNormalId]="client.id"
                                    [frequenciesFromThisSubRoute]="subRoute.frequencies || []"
                                    (frequencyUpdated)="onFrequencyUpdated($event)"
                                    (refreshRequested)="onRefreshRequested()">
                                  </app-client-frequency-display>
                                  @if (!hasFrequencies(subRoute)) {
                                    <span class="no-frequency-badge">Sin frecuencia</span>
                                  }
                                </div>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>

                    <!-- No Routes Message -->
                    @if (!client.subRoutes || client.subRoutes.length === 0) {
                      <div class="text-center py-8 bg-white rounded border border-gray-200">
                        <mat-icon class="text-gray-300 !w-12 !h-12 !text-[48px]">route</mat-icon>
                        <p class="mt-2 text-gray-500">Este cliente no tiene rutas asignadas</p>
                      </div>
                    }
                  </div>
                </div>
              </td>
            </ng-container>

            <!-- Header and Row Definitions -->
            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true" class="bg-gray-50"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"
                [class.expanded-row]="expandedElement === row"></tr>
            <tr mat-row *matRowDef="let row; columns: ['expandedDetail']"
                class="detail-row"></tr>
          </table>
        </div>

        <!-- Empty State -->
        @if (dataSource.data.length === 0 && !isLoading) {
          <div class="flex-1 flex items-center justify-center">
            <div class="text-center py-12 text-gray-500">
              <mat-icon class="!w-16 !h-16 !text-[64px] mx-auto mb-4 text-gray-300">search</mat-icon>
              @if (searchControl.value) {
                <h3 class="text-lg font-medium mb-2">No se encontraron clientes</h3>
                <p class="text-sm">No hay resultados para "{{ searchControl.value }}"</p>
              } @else {
                <h3 class="text-lg font-medium mb-2">Buscar clientes</h3>
                <p class="text-sm">Utiliza el campo de búsqueda para encontrar clientes</p>
              }
            </div>
          </div>
        }
      </div>

      <!-- Pagination -->
      @if (dataSource.data.length > 0) {
        <mat-paginator
          [length]="totalItems"
          [pageSize]="pageSize"
          [pageIndex]="currentPage"
          [pageSizeOptions]="pageSizeOptions"
          (page)="onPageChange($event)"
          showFirstLastButtons
          class="border-t border-gray-200">
        </mat-paginator>
      }
    }
  </div>
</div>
